// First-time context prompts (§9.1 step 3) as a sticker under the action: "Shove: [LMB]", "Grab a
// bean: [RMB] … then throw: [RMB]", "Push back to recover!". The logic (what to show, uses,
// sessions) is WP9a's `profile/prompts.ts`; this draws it with live device glyphs and plays a
// check-mark pop when a prompt is learned. Used by the HUD (Practice), the Lobby and the menu.
import type { FrameView } from '../../game/FrameView';
import type { PromptView as Prompt } from '../../profile/prompts';
import type { UiDeps } from '../deps';
import { glyphCap } from '../glyphView';
import { icon } from '../icons';
import { el, replayClass, setHidden, toggleClass } from '../ui';

export interface PromptLayer { readonly el: HTMLElement; update(view: FrameView): void }

export function createPromptView(deps: UiDeps, cls = ''): PromptLayer {
  const body = el('span', { class: 'fp-body' });
  const pips = el('span', { class: 'fp-pips' });
  const root = el('div', { class: `first-prompt ${cls}`, attrs: { role: 'status', 'aria-live': 'polite' } },
    el('span', { class: 'fp-tip', text: 'TIP' }), body, pips, el('span', { class: 'fp-done' }, icon('checkBadge')));
  root.hidden = true;
  let key = '';

  function render(p: Prompt): void {
    body.replaceChildren(...p.parts.map((part) => (typeof part === 'string' ? el('span', { text: part }) : glyphCap(part))));
    pips.replaceChildren(...Array.from({ length: p.goal }, (_, i) => el('i', { class: i < p.uses ? 'on' : '' })));
  }

  return {
    el: root,
    update(view) {
      const prompts = deps.services.prompts;
      const p = prompts ? prompts.update(view) : null;
      setHidden(root, p === null);
      if (!p) {
        key = '';
        return;
      }
      const k = `${p.id}:${p.uses}:${p.done}:${p.text}:${deps.services.input.device}`;
      if (k === key) return;
      const fresh = !key.startsWith(`${p.id}:`);
      key = k;
      render(p);
      toggleClass(root, 'done', p.done);
      if (fresh) replayClass(root, 'enter');
      else replayClass(root, 'tick');
    },
  };
}
